import {useMemo, useRef, useEffect} from 'react';
import css from './mdViewer.module.scss';
import {parse as mdparse} from 'marked';
import ImageCarousel from '../blog/ImageCarousel';
import VideoPlayer from '../blog/VideoPlayer';

function parseGalleryBlock(body) {
  return body
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const parts = line.split('|').map(s => s.trim());
      return {src: parts[0], alt: parts[1] || ''};
    });
}

function parseVideoBlock(body) {
  return body
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const colonIdx = line.indexOf(':');
      if (colonIdx === -1) return null;
      const platform = line.slice(0, colonIdx).trim().toLowerCase();
      const url = line.slice(colonIdx + 1).trim();
      return {platform, url};
    })
    .filter(Boolean);
}

function splitContent(raw) {
  if (!raw) return [];

  const codeFences = [];
  const withoutCode = raw.replace(/^```[\s\S]*?^```/gm, (match) => {
    codeFences.push(match);
    return `\n%%CODEFENCE_${codeFences.length - 1}%%\n`;
  });

  const segments = [];
  let lastIndex = 0;
  const regex = /^:::(\w+)\s*\n([\s\S]*?)^:::\s*$/gm;
  let match;

  while ((match = regex.exec(withoutCode)) !== null) {
    if (match.index > lastIndex) {
      const text = withoutCode.slice(lastIndex, match.index).trim();
      if (text) segments.push({type: 'markdown', content: text});
    }

    const directive = match[1].toLowerCase();
    const body = match[2];

    if (directive === 'gallery') {
      segments.push({type: 'gallery', images: parseGalleryBlock(body)});
    } else if (directive === 'video') {
      segments.push({type: 'video', sources: parseVideoBlock(body)});
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < withoutCode.length) {
    const text = withoutCode.slice(lastIndex).trim();
    if (text) segments.push({type: 'markdown', content: text});
  }

  return segments.map(seg => {
    if (seg.type === 'markdown') {
      let content = seg.content;
      codeFences.forEach((fence, idx) => {
        content = content.replace(`%%CODEFENCE_${idx}%%`, fence);
      });
      return {...seg, content};
    }
    return seg;
  });
}

function MarkdownSegment({content}) {
  const ref = useRef(null);
  const html = useMemo(() => mdparse(content), [content]);

  useEffect(() => {
    const node = ref.current;
    if (node) node.innerHTML = html;
    return () => {
      if (node) node.innerHTML = '';
    };
  }, [html]);

  return <div ref={ref} className={css.md} style={{padding: 0}}/>;
}

export default function RichMdViewer({file, basePath, isShort}) {
  const segments = useMemo(() => splitContent(file), [file]);

  if (!file) return null;

  if (isShort) {
    return (
      <MarkdownSegment content={file}/>
    );
  }

  return (
    <div style={{padding: '80px 0'}}>
      {segments.map((seg, i) => {
        if (seg.type === 'markdown') {
          return <MarkdownSegment key={i} content={seg.content}/>;
        }
        if (seg.type === 'gallery') {
          return <ImageCarousel key={i} images={seg.images} basePath={basePath}/>;
        }
        if (seg.type === 'video') {
          return <VideoPlayer key={i} sources={seg.sources} basePath={basePath}/>;
        }
        return null;
      })}
    </div>
  );
}
